'use client';

import React from 'react';
import { Clock, BookOpen, Users, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { PDStats, PD_REQUIREMENTS, CATEGORY_INFO } from './types';
import { formatHours } from './utils';

interface PDProgressSectionProps {
  stats: PDStats;
}

export function PDProgressSection({ stats }: PDProgressSectionProps) {
  const progressPercent = Math.min(100, (stats.totalHours / stats.targetHours) * 100);
  const isComplete = stats.totalHours >= stats.targetHours;

  // Calculate months remaining (round up to nearest month)
  const monthsRemaining = Math.ceil(stats.daysRemaining / 30);

  // Find top category
  const topCategory = Object.entries(stats.byCategory)
    .sort(([, a], [, b]) => b - a)[0];

  return (
    <div className="space-y-4">
      {/* Main Progress Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
          {/* Progress Ring */}
          <div className="relative flex-shrink-0">
            <svg
              className="w-32 h-32 transform -rotate-90"
              role="img"
              aria-label={`Progress: ${formatHours(stats.totalHours)} of ${stats.targetHours} hours completed (${Math.round(progressPercent)}%)`}
            >
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="12"
              />
              {/* Progress circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                fill="none"
                stroke={isComplete ? '#22c55e' : '#9333ea'}
                strokeWidth="12"
                strokeDasharray={`${(progressPercent / 100) * 352} 352`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
              <span className="text-3xl font-bold text-gray-900">
                {formatHours(stats.totalHours)}
              </span>
              <span className="text-sm text-gray-500">
                of {stats.targetHours}h
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex-1 w-full grid grid-cols-2 gap-3 sm:gap-4">
            <StatCard
              icon={Clock}
              label="Hours Remaining"
              value={formatHours(stats.hoursRemaining)}
              suffix="h"
              color={stats.hoursRemaining === 0 ? 'green' : 'gray'}
            />
            <StatCard
              icon={BookOpen}
              label="Activities"
              value={stats.activityCount}
              color="purple"
            />
            <StatCard
              icon={Calendar}
              label="Months Left"
              value={monthsRemaining}
              color={monthsRemaining < 3 ? 'orange' : 'blue'}
            />
            <StatCard
              icon={Users}
              label="Top Category"
              value={topCategory ? CATEGORY_INFO[topCategory[0] as keyof typeof CATEGORY_INFO].label : '—'}
              isText
              color="gray"
            />
          </div>
        </div>
      </div>

      {/* Type Breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Didactic Hours */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span className="font-medium text-gray-900">Didactic Hours</span>
            </div>
            {stats.hasDidactic ? (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>Requirement met</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Need at least 1</span>
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {formatHours(stats.byType.didactic)}
            </span>
            <span className="text-gray-500">hours</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Courses, workshops, conferences, webinars, reading, research, teaching
          </p>
        </div>

        {/* Experiential Hours */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span className="font-medium text-gray-900">Experiential Hours</span>
            </div>
            {stats.hasExperiential ? (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>Requirement met</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Need at least 1</span>
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {formatHours(stats.byType.experiential)}
            </span>
            <span className="text-gray-500">hours</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Peer consultation, clinical supervision, personal therapy, study groups
          </p>
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  suffix?: string;
  isText?: boolean;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'gray';
}

const colorClasses = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-green-50 text-green-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-orange-50 text-orange-600',
  gray: 'bg-gray-50 text-gray-600',
};

function StatCard({ icon: Icon, label, value, suffix, isText, color }: StatCardProps) {
  return (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`font-semibold text-gray-900 ${isText ? 'text-sm' : 'text-lg'}`}>
          {value}
          {suffix && <span className="text-gray-500 font-normal">{suffix}</span>}
        </p>
      </div>
    </div>
  );
}
